import { useAuth } from './useAuth';
import { SellerSettings } from '@shopify-multiseller/graphql/partner/types';

/**
 * Why settings could not be read.
 *
 * `signed-out` is a routing fault rather than a data fault: there is no
 * session at all, so no scope of settings exists to fall back to and the route
 * should never have rendered. Naming it apart from `no-settings` is what lets
 * the router's error boundary say "you're signed out" instead of "something
 * went wrong" — the difference between a seller knowing to sign in and a
 * seller leaving.
 */
export type SettingsUnavailableReason = 'signed-out' | 'no-settings';

export class SettingsUnavailableError extends Error {
  readonly reason: SettingsUnavailableReason;

  constructor(reason: SettingsUnavailableReason) {
    super(
      reason === 'signed-out'
        ? 'useSettings: no authenticated user, so neither seller- nor shop-scope settings exist'
        : 'useSettings: the authenticated user carries neither seller- nor shop-scope settings',
    );
    this.name = 'SettingsUnavailableError';
    this.reason = reason;
    // Subclassing Error loses the prototype chain when downlevelled, and the
    // error boundary matches on `instanceof`.
    Object.setPrototypeOf(this, SettingsUnavailableError.prototype);
  }
}

export const useSettings = (): SellerSettings  => {
  const auth = useAuth();
  // Established sellers read their seller-effective settings. A self-serve
  // registrant has no seller record yet (CompleteRegistration creates it),
  // so fall back to the shop-scope settings on User.settings — the two types
  // are field-for-field identical, resolved with and without a seller.
  // Without this, every pre-registration screen that reads settings threw
  // straight into the router error boundary.
  const settings = auth?.user?.seller?.settings ?? auth?.user?.settings;
  if (!settings) {
    // Still a throw, deliberately. Every caller reads fields off the result
    // unguarded — CompleteRegistration reads settings.productShipping and
    // settings.sellerLocation directly — so returning a partial object moves
    // the same crash one line down, and returning defaults would draw a
    // registration form that a signed-out visitor cannot submit.
    //
    // What changed with MPPRO-154 is where it lands: every route in every
    // partner-web router now carries RouteErrorBoundary, so this can no
    // longer reach react-router's DefaultErrorComponent and print its stack
    // to the screen.
    throw new SettingsUnavailableError(auth?.user ? 'no-settings' : 'signed-out');
  }
  return settings as SellerSettings;
};
